import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  FileBarChart,
  BarChart,
  DollarSign,
  Star
} from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

// PDF generation
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Excel generation
import * as XLSX from 'xlsx';
import LoadingSpinner from '../../components/LoadingSpinner';

const Reports = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('earnings');
  const [timeframe, setTimeframe] = useState('last-3-months');
  const [campaigns, setCampaigns] = useState([]);
  const [payments, setPayments] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [chartData, setChartData] = useState({});

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser]);

  useEffect(() => {
    if (campaigns.length > 0 || payments.length > 0 || portfolio.length > 0) {
      generateReportData();
    }
  }, [reportType, timeframe, campaigns, payments, portfolio]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch campaigns the influencer has participated in
      const campaignParticipantsRef = collection(db, 'campaignParticipants');
      const campaignParticipantsQuery = query(campaignParticipantsRef, where('influencerId', '==', currentUser.uid));
      const campaignParticipantsSnapshot = await getDocs(campaignParticipantsQuery);
      
      const campaignIds = [];
      const campaignsData = [];
      
      // Get all campaign IDs the influencer has participated in
      campaignParticipantsSnapshot.forEach(doc => {
        const data = doc.data();
        campaignIds.push(data.campaignId);
      });
      
      // Fetch details for each campaign
      for (const campaignId of campaignIds) {
        const campaignsRef = collection(db, 'campaigns');
        const campaignsQuery = query(campaignsRef, where('id', '==', campaignId));
        const campaignsSnapshot = await getDocs(campaignsQuery);
        
        campaignsSnapshot.forEach(doc => {
          campaignsData.push({
            id: doc.id,
            ...doc.data(),
            // Add participation data
            participation: campaignParticipantsSnapshot.docs
              .find(partDoc => partDoc.data().campaignId === campaignId)?.data() || {}
          });
        });
      }
      
      setCampaigns(campaignsData);
      
      // Fetch payments for the influencer
      const paymentsRef = collection(db, 'payments');
      const paymentsQuery = query(paymentsRef, where('influencerId', '==', currentUser.uid));
      const paymentsSnapshot = await getDocs(paymentsQuery);
      const paymentsData = paymentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPayments(paymentsData);
      
      // Fetch portfolio items
      const portfolioRef = collection(db, 'portfolioItems');
      const portfolioQuery = query(portfolioRef, where('influencerId', '==', currentUser.uid));
      const portfolioSnapshot = await getDocs(portfolioQuery);
      const portfolioData = portfolioSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPortfolio(portfolioData);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data for reports:', error);
      setLoading(false);
    }
  };

  const generateReportData = () => {
    // Filter data based on selected timeframe
    const { startDate, endDate } = getTimeframeDates(timeframe);
    
    // Generate report data based on report type
    switch (reportType) {
      case 'earnings':
        generateEarningsReport(startDate, endDate);
        break;
      case 'campaign-performance':
        generateCampaignPerformanceReport(startDate, endDate);
        break;
      // Portfolio analytics report removed as requested
      case 'ratings-reviews':
        generateRatingsReviewsReport(startDate, endDate);
        break;
      default:
        setReportData([]);
    }
  };
  
  const generateEarningsReport = (startDate, endDate) => {
    // Filter payments within the timeframe
    const filteredPayments = payments.filter(payment => {
      const paymentDate = payment.processedAt?.toDate ? payment.processedAt.toDate() : new Date(payment.processedAt);
      return paymentDate >= startDate && paymentDate <= endDate;
    });
    
    // Group payments by month
    const monthlyEarnings = {};
    
    filteredPayments.forEach(payment => {
      const paymentDate = payment.processedAt?.toDate ? payment.processedAt.toDate() : new Date(payment.processedAt);
      const monthYear = format(paymentDate, 'MMM yyyy');
      
      if (!monthlyEarnings[monthYear]) {
        monthlyEarnings[monthYear] = {
          month: monthYear,
          totalEarned: 0,
          paymentCount: 0,
          campaigns: new Set(),
          brands: new Set(),
          pending: 0,
          processed: 0,
          completed: 0
        };
      }
      
      monthlyEarnings[monthYear].totalEarned += (payment.amount || 0);
      monthlyEarnings[monthYear].paymentCount += 1;
      monthlyEarnings[monthYear].campaigns.add(payment.campaignId);
      monthlyEarnings[monthYear].brands.add(payment.brandId);
      
      // Track payment status
      if (payment.status === 'pending') {
        monthlyEarnings[monthYear].pending += payment.amount || 0;
      } else if (payment.status === 'processing') {
        monthlyEarnings[monthYear].processed += payment.amount || 0;
      } else if (payment.status === 'completed') {
        monthlyEarnings[monthYear].completed += payment.amount || 0;
      }
    });

    // Convert to array and sort by date
    const data = Object.values(monthlyEarnings)
      .map(earning => ({
        month: earning.month,
        totalEarned: earning.totalEarned,
        paymentCount: earning.paymentCount,
        campaignCount: earning.campaigns.size,
        brandCount: earning.brands.size,
        pending: earning.pending,
        processed: earning.processed,
        completed: earning.completed,
        averagePayment: earning.totalEarned / earning.paymentCount || 0
      }))
      .sort((a, b) => {
        const dateA = new Date(a.month);
        const dateB = new Date(b.month);
        return dateA - dateB;
      });

    setReportData(data);

    // Generate chart data
    const chartLabels = data.map(item => item.month);
    const chartDatasets = [
      {
        label: 'Total Earnings',
        data: data.map(item => item.totalEarned),
        backgroundColor: 'rgba(59, 91, 165, 0.5)',
        borderColor: 'rgba(59, 91, 165, 1)',
        borderWidth: 1
      },
      {
        label: 'Completed Payments',
        data: data.map(item => item.completed),
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      },
      {
        label: 'Pending Payments',
        data: data.map(item => item.pending),
        backgroundColor: 'rgba(255, 159, 64, 0.5)',
        borderColor: 'rgba(255, 159, 64, 1)',
        borderWidth: 1
      }
    ];

    setChartData({
      labels: chartLabels,
      datasets: chartDatasets
    });
  };

  const generateCampaignPerformanceReport = (startDate, endDate) => {
    // Filter campaigns within the timeframe
    const filteredCampaigns = campaigns.filter(campaign => {
      const campaignStartDate = campaign.startDate?.toDate ? campaign.startDate.toDate() : new Date(campaign.startDate);
      return campaignStartDate >= startDate && campaignStartDate <= endDate;
    });

    // Generate report data
    const data = filteredCampaigns.map(campaign => {
      // Get payment for this campaign
      const campaignPayment = payments.find(payment => payment.campaignId === campaign.id);
      
      // Get portfolio items for this campaign (if any)
      const campaignPortfolio = portfolio.filter(item => item.campaignId === campaign.id);
      
      // Calculate engagement metrics (mock data for now)
      const likes = Math.floor(Math.random() * 5000);
      const comments = Math.floor(Math.random() * 500);
      const shares = Math.floor(Math.random() * 200);
      
      return {
        campaignId: campaign.id,
        campaignName: campaign.name || 'Unnamed Campaign',
        brandName: campaign.brandName || 'Unknown Brand',
        startDate: campaign.startDate?.toDate ? format(campaign.startDate.toDate(), 'MMM dd, yyyy') : 'N/A',
        endDate: campaign.endDate?.toDate ? format(campaign.endDate.toDate(), 'MMM dd, yyyy') : 'N/A',
        status: campaign.status || 'Unknown',
        paymentStatus: campaignPayment?.status || 'Not processed',
        paymentAmount: campaignPayment?.amount || 0,
        contentCount: campaignPortfolio.length,
        engagementRate: ((likes + comments + shares) / 10000 * 100).toFixed(2) + '%',
        likes,
        comments,
        shares
      };
    });

    setReportData(data);

    // Generate chart data
    const chartLabels = data.map(item => item.campaignName);
    const chartDatasets = [
      {
        label: 'Payment Amount',
        data: data.map(item => item.paymentAmount),
        backgroundColor: 'rgba(59, 91, 165, 0.5)'
      },
      {
        label: 'Likes',
        data: data.map(item => item.likes),
        backgroundColor: 'rgba(75, 192, 192, 0.5)'
      },
      {
        label: 'Comments',
        data: data.map(item => item.comments),
        backgroundColor: 'rgba(255, 159, 64, 0.5)'
      }
    ];

    setChartData({
      labels: chartLabels,
      datasets: chartDatasets
    });
  };

  const generateRatingsReviewsReport = (startDate, endDate) => {
    // Filter campaigns within the timeframe
    const filteredCampaigns = campaigns.filter(campaign => {
      const campaignEndDate = campaign.endDate?.toDate ? campaign.endDate.toDate() : new Date(campaign.endDate);
      return campaignEndDate >= startDate && campaignEndDate <= endDate;
    });

    // Generate ratings data (using mock data since we don't have actual ratings in the data model)
    const data = filteredCampaigns.map(campaign => {
      // Mock ratings data
      const rating = (3 + Math.random() * 2).toFixed(1); // Random rating between 3.0 and 5.0
      const reviewCount = Math.floor(Math.random() * 10) + 1; // Random number of reviews
      
      return {
        campaignId: campaign.id,
        campaignName: campaign.name || 'Unnamed Campaign',
        brandName: campaign.brandName || 'Unknown Brand',
        completionDate: campaign.endDate?.toDate ? format(campaign.endDate.toDate(), 'MMM dd, yyyy') : 'N/A',
        rating: parseFloat(rating),
        reviewCount,
        feedback: generateMockFeedback(parseFloat(rating)),
        responseTime: Math.floor(Math.random() * 24) + 1 + ' hours', // Random response time
        contentQuality: (3 + Math.random() * 2).toFixed(1),
        professionalism: (3 + Math.random() * 2).toFixed(1),
        communication: (3 + Math.random() * 2).toFixed(1)
      };
    });

    setReportData(data);

    // Generate chart data
    const chartLabels = data.map(item => item.campaignName);
    const chartDatasets = [
      {
        label: 'Rating',
        data: data.map(item => item.rating),
        backgroundColor: 'rgba(59, 91, 165, 0.5)'
      },
      {
        label: 'Content Quality',
        data: data.map(item => item.contentQuality),
        backgroundColor: 'rgba(75, 192, 192, 0.5)'
      },
      {
        label: 'Professionalism',
        data: data.map(item => item.professionalism),
        backgroundColor: 'rgba(255, 159, 64, 0.5)'
      },
      {
        label: 'Communication',
        data: data.map(item => item.communication),
        backgroundColor: 'rgba(153, 102, 255, 0.5)'
      }
    ];

    setChartData({
      labels: chartLabels,
      datasets: chartDatasets
    });
  };

  // Helper function to generate mock feedback based on rating
  const generateMockFeedback = (rating) => {
    if (rating >= 4.5) {
      return 'Excellent work! Very professional and delivered high-quality content.';
    } else if (rating >= 4.0) {
      return 'Great job on this campaign. Content was well-received.';
    } else if (rating >= 3.5) {
      return 'Good work overall. Some minor improvements could be made.';
    } else {
      return 'Satisfactory work. Please focus on improving content quality.';
    }
  };

  const getTimeframeDates = (timeframe) => {
    const now = new Date();
    let startDate, endDate;

    switch (timeframe) {
      case 'last-month':
        startDate = startOfMonth(subMonths(now, 1));
        endDate = endOfMonth(subMonths(now, 1));
        break;
      case 'last-3-months':
        startDate = startOfMonth(subMonths(now, 3));
        endDate = now;
        break;
      case 'last-6-months':
        startDate = startOfMonth(subMonths(now, 6));
        endDate = now;
        break;
      case 'last-year':
        startDate = startOfMonth(subMonths(now, 12));
        endDate = now;
        break;
      default:
        startDate = startOfMonth(subMonths(now, 3));
        endDate = now;
    }

    return { startDate, endDate };
  };

  // Render functions will be implemented in later sections

  // Download report as PDF
  const downloadPDF = () => {
    if (reportData.length === 0) return;
    
    const doc = new jsPDF();
    const title = `${reportType.replace('-', ' ').toUpperCase()} REPORT`;
    const subtitle = `Timeframe: ${timeframe.replace('-', ' ')} (${format(getTimeframeDates(timeframe).startDate, 'MMM dd, yyyy')} - ${format(getTimeframeDates(timeframe).endDate, 'MMM dd, yyyy')})`;
    
    // Add title
    doc.setFontSize(18);
    doc.setTextColor(59, 91, 165);
    doc.text(title, 105, 20, { align: 'center' });
    
    // Add subtitle
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.text(subtitle, 105, 30, { align: 'center' });
    
    // Add table
    const tableColumn = Object.keys(reportData[0]);
    const tableRows = reportData.map(item => Object.values(item));
    
    doc.autoTable({
      head: [tableColumn.map(key => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()))],
      body: tableRows,
      startY: 40,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 3
      },
      headStyles: {
        fillColor: [59, 91, 165],
        textColor: 255
      }
    });
    
    doc.save(`${reportType}-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };
  
  // Download report as Excel
  const downloadExcel = () => {
    if (reportData.length === 0) return;
    
    const worksheet = XLSX.utils.json_to_sheet(reportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, reportType.replace('-', ' '));
    XLSX.writeFile(workbook, `${reportType}-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Reports</h2>
        <p className="text-sm text-gray-600">Generate and download comprehensive reports about your performance and earnings</p>
      </div>

      <div className="bg-white shadow rounded-lg mb-8">
        <div className="p-6">
          {/* Report type selection */}
          <div className="mb-8">
            <h3 className="text-base font-medium text-gray-800 mb-3">Select Report Type</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div 
                className={`p-4 border rounded-lg cursor-pointer transition-all ${reportType === 'earnings' ? 'border-primary bg-blue-50' : 'border-gray-200 hover:border-primary'}`}
                onClick={() => setReportType('earnings')}
              >
                <div className="flex items-center">
                  <DollarSign className="h-8 w-8 text-primary mr-3" />
                  <div>
                    <h4 className="text-sm font-medium">Earnings Report</h4>
                    <p className="text-sm text-gray-500">Track your earnings over time</p>
                  </div>
                </div>
              </div>
              
              <div 
                className={`p-4 border rounded-lg cursor-pointer transition-all ${reportType === 'campaign-performance' ? 'border-primary bg-blue-50' : 'border-gray-200 hover:border-primary'}`}
                onClick={() => setReportType('campaign-performance')}
              >
                <div className="flex items-center">
                  <BarChart className="h-8 w-8 text-primary mr-3" />
                  <div>
                    <h4 className="text-sm font-medium">Campaign Performance</h4>
                    <p className="text-sm text-gray-500">Analyze your campaign metrics</p>
                  </div>
                </div>
              </div>
              
              <div 
                className={`p-4 border rounded-lg cursor-pointer transition-all ${reportType === 'ratings-reviews' ? 'border-primary bg-blue-50' : 'border-gray-200 hover:border-primary'}`}
                onClick={() => setReportType('ratings-reviews')}
              >
                <div className="flex items-center">
                  <Star className="h-8 w-8 text-primary mr-3" />
                  <div>
                    <h4 className="text-sm font-medium">Ratings & Reviews</h4>
                    <p className="text-sm text-gray-500">View your performance ratings</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Timeframe selection */}
          <div className="mb-8">
            <h3 className="text-base font-medium text-gray-800 mb-3">Select Timeframe</h3>
            <div className="flex flex-wrap gap-2">
              <button 
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${timeframe === 'last-month' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                onClick={() => setTimeframe('last-month')}
              >
                Last Month
              </button>
              <button 
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${timeframe === 'last-3-months' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                onClick={() => setTimeframe('last-3-months')}
              >
                Last 3 Months
              </button>
              <button 
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${timeframe === 'last-6-months' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                onClick={() => setTimeframe('last-6-months')}
              >
                Last 6 Months
              </button>
              <button 
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${timeframe === 'last-year' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                onClick={() => setTimeframe('last-year')}
              >
                Last Year
              </button>
            </div>
          </div>
          
          {loading ? (
            null
          ) : reportData.length > 0 ? (
            <div className="mb-8">
              {/* Report content */}
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-base font-medium text-gray-800">
                  {reportType === 'earnings' && 'Earnings Report'}
                  {reportType === 'campaign-performance' && 'Campaign Performance Report'}
                  {reportType === 'ratings-reviews' && 'Ratings & Reviews Report'}
                </h3>
                <div className="flex gap-2">
                  <button 
                    className="flex items-center px-2.5 py-1.5 text-sm bg-primary text-white rounded-md hover:bg-blue-700"
                    onClick={downloadPDF}
                  >
                    PDF
                  </button>
                  <button 
                    className="flex items-center px-2.5 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                    onClick={downloadExcel}
                  >
                    Excel
                  </button>
                </div>
              </div>
              
              {/* Table display */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {reportData.length > 0 && Object.keys(reportData[0]).map((key, index) => (
                        <th 
                          key={index}
                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.map((item, index) => (
                      <tr key={index}>
                        {Object.values(item).map((value, idx) => (
                          <td key={idx} className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-10">
              <FileBarChart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-sm text-gray-500">Select a report type and timeframe to generate a report</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
